import { OktaAuth } from '@okta/okta-auth-js';
import { UserInfo } from '../types/user-info';
import { getOktaAuthServerId, getOktaClientId, getOktaDomain, GetOktaUserClaims } from '../utils/config';
import { getItem, STORAGE_KEYS } from './local-storage';
import { decodeToken } from './auth';

const oktaAuth = new OktaAuth({
  issuer: `https://${getOktaDomain()}/oauth2/${getOktaAuthServerId()}`,
  clientId: getOktaClientId(),
  redirectUri: `${window.location.origin}/login/callback`,
  scopes: ['openid', 'email', 'profile'],
});

const getDefaultUserInfo = (): UserInfo => ({
  eid: '',
  email: '',
  name: '',
  given_name: '',
  family_name: '',
  client_company: '',
  client_company_account: '',
  license_entitlement: '',
  subscriptions: 0,
  email_domain: '',
  groups: [] as [string?],
});

export const getDowJonesUserInfo = (access_token: string): UserInfo => {
  const decodedToken = decodeToken(access_token);
  if (!decodedToken) {
    console.error('Failed to decode Dow Jones ID token.');
    return getDefaultUserInfo();
  }

  return {
    eid: decodedToken.pib.eid.trim() ?? '',
    email: decodedToken.email ?? '',
    name: decodedToken.name ?? '',
    given_name: decodedToken.given_name ?? '',
    family_name: decodedToken.family_name ?? '',
    client_company: decodedToken.organization ?? '',
    client_company_account: decodedToken.division ?? '',
    license_entitlement: decodedToken.cost_center ?? '',
    subscriptions: 0, // Default to 0 since no user info endpoint exists
    email_domain: decodedToken.email?.split('@')[1] ?? '',
    groups: decodedToken.groups ?? ([] as [string?]),
  };
};

export const getOktaUserInfo = async (): Promise<UserInfo> => {
  const initial_token = oktaAuth.getAccessToken() ?? '';
  if (!initial_token) {
    console.error('No Okta access token found.');
    return getDefaultUserInfo();
  }

  try {
    const accesstoken = oktaAuth.token.decode(initial_token);
    const ui = await GetOktaUserClaims(oktaAuth);
    let domain_a = ui?.email?.split('@') || [''];

    return {
      eid: '',
      email: ui?.email || '',
      name: ui?.name || '',
      given_name: ui?.given_name || '',
      family_name: ui?.family_name || '',
      client_company: (accesstoken?.payload?.Organization as string) || '',
      client_company_account: (accesstoken?.payload?.Division as string) || '',
      license_entitlement: (accesstoken?.payload?.CostCenter as string) || '',
      subscriptions: 0, // we need to get this from the validate_user endpoint
      email_domain: domain_a[domain_a.length - 1] || '',
      groups: (accesstoken?.payload?.Groups as [string?]) || ([] as [string?]),
    };
  } catch (err) {
    console.error('Error fetching Okta user info:', err);
    return getDefaultUserInfo();
  }
};

export const getUserInfo = async (): Promise<UserInfo> => {
  try {
    const djAccessToken = getItem(STORAGE_KEYS.DJ_ACCESS_TOKEN);
    if (djAccessToken) {
      return getDowJonesUserInfo(djAccessToken);
    }

    return await getOktaUserInfo();
  } catch (err) {
    console.error('Error in getUserInfo:', err);
    return getDefaultUserInfo();
  }
};
